import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Tabs, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import InteractionTimeline from './InteractionTimeline';
import InteractionForm from './InteractionForm';
import TaskTable from './TaskTable';
import TaskForm from './TaskForm';
import { useInteractions } from '../hooks/useInteractions';
import { useTasks } from '../hooks/useTasks';
import { CrmInteractionDTO, CrmTaskDTO } from '../types';

const { Text } = Typography;

interface Props {
    branch: string;
    custNo: string;
}

const CustomerCrmTab: React.FC<Props> = ({ branch, custNo }) => {
    const {
        fetchByCustomer: fetchInteractions,
        create: createInteraction,
        update: updateInteraction,
        remove: deleteInteraction,
        isSubmitting: interactionSubmitting,
    } = useInteractions();
    const {
        fetchByCustomer: fetchTasks,
        create: createTask,
        complete: completeTask,
        cancel: cancelTask,
        isSubmitting: taskSubmitting,
    } = useTasks();

    const [interactions, setInteractions] = useState<CrmInteractionDTO[]>([]);
    const [tasks, setTasks] = useState<CrmTaskDTO[]>([]);
    const [interactionModal, setInteractionModal] = useState(false);
    const [taskModal, setTaskModal] = useState(false);
    const [editingInteraction, setEditingInteraction] = useState<CrmInteractionDTO | undefined>();

    const loadData = useCallback(async () => {
        const [interactionData, taskData] = await Promise.all([
            fetchInteractions(branch, custNo),
            fetchTasks(branch, custNo),
        ]);
        setInteractions(interactionData);
        setTasks(taskData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branch, custNo]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleLogInteraction = async (values: CrmInteractionDTO) => {
        const ok = editingInteraction?.id
            ? await updateInteraction(editingInteraction.id, { ...values, custNo })
            : await createInteraction({ ...values, custNo });
        if (ok) {
            setInteractionModal(false);
            setEditingInteraction(undefined);
            loadData();
        }
    };

    const handleDeleteInteraction = async (id: number) => {
        await deleteInteraction(id);
        loadData();
    };

    const handleCreateTask = async (values: CrmTaskDTO) => {
        const ok = await createTask({ ...values, custNo });
        if (ok) {
            setTaskModal(false);
            loadData();
        }
    };

    const handleCompleteTask = async (id: number) => {
        await completeTask(id);
        loadData();
    };

    const handleCancelTask = async (id: number) => {
        await cancelTask(id);
        loadData();
    };

    const openTasks = tasks.filter(t => t.status === 'OPEN' || t.status === 'IN_PROGRESS');

    return (
        <>
            <Tabs
                defaultActiveKey="interactions"
                items={[
                    {
                        key: 'interactions',
                        label: `Interactions (${interactions.length})`,
                        children: (
                            <div>
                                <div className="flex justify-end mb-4">
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            setEditingInteraction(undefined);
                                            setInteractionModal(true);
                                        }}
                                    >
                                        Log Interaction
                                    </Button>
                                </div>
                                <InteractionTimeline
                                    interactions={interactions}
                                    onEdit={(i) => { setEditingInteraction(i); setInteractionModal(true); }}
                                    onDelete={handleDeleteInteraction}
                                />
                            </div>
                        ),
                    },
                    {
                        key: 'tasks',
                        label: `Tasks (${openTasks.length} open)`,
                        children: (
                            <div>
                                <div className="flex justify-end mb-4">
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setTaskModal(true)}
                                    >
                                        Add Task
                                    </Button>
                                </div>
                                {tasks.length === 0
                                    ? <Text type="secondary">No tasks for this customer.</Text>
                                    : (
                                        <TaskTable
                                            tasks={tasks}
                                            showCustomer={false}
                                            onComplete={handleCompleteTask}
                                            onCancel={handleCancelTask}
                                        />
                                    )
                                }
                            </div>
                        ),
                    },
                ]}
            />

            <Modal
                open={interactionModal}
                title={editingInteraction ? 'Edit Interaction' : 'Log Interaction'}
                footer={null}
                onCancel={() => { setInteractionModal(false); setEditingInteraction(undefined); }}
                width={600}
                destroyOnClose
            >
                <InteractionForm
                    custNo={custNo}
                    initialValues={editingInteraction}
                    isSubmitting={interactionSubmitting}
                    onSubmit={handleLogInteraction}
                    onCancel={() => { setInteractionModal(false); setEditingInteraction(undefined); }}
                />
            </Modal>

            <Modal
                open={taskModal}
                title="Add Task"
                footer={null}
                onCancel={() => setTaskModal(false)}
                width={500}
                destroyOnClose
            >
                <TaskForm
                    custNo={custNo}
                    isSubmitting={taskSubmitting}
                    onSubmit={handleCreateTask}
                    onCancel={() => setTaskModal(false)}
                />
            </Modal>
        </>
    );
};

export default CustomerCrmTab;
